import React, { useEffect, useReducer } from 'react';
import { toast } from 'sonner';
import { fetchProfile } from '@/lib/api/profile';
import { parseSupabaseError } from '@/lib/errors';
import type { FriendlyError } from '@/lib/errors';
import { supabase } from '@/lib/supabase';
import { captureFriendlyError, setSentryUser } from '@/lib/sentry';
import {
  identifyUser,
  resetAnalytics,
  track,
} from '@/lib/analytics';
import { logger } from '@/lib/logger';
import { AuthContext } from './useAuth';
import type { AuthState } from './useAuth';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, updateState] = useReducer(
    (prev: AuthState, next: Partial<AuthState>) => ({ ...prev, ...next }),
    {
      user: null,
      session: null,
      profile: null,
      isAuthLoading: true,
      isProfileLoading: false,
    }
  );

  const loadProfile = async (userId: string) => {
    updateState({ isProfileLoading: true });
    try {
      const profile = await fetchProfile(userId);
      updateState({ profile, isProfileLoading: false });
    } catch (err) {
      updateState({ isProfileLoading: false });
      throw err;
    }
  };

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        if (!mounted) return;

        const session = data.session;
        updateState({
          session,
          user: session?.user ?? null,
          isAuthLoading: false,
        });

        if (session?.user) {
          setSentryUser({ id: session.user.id, email: session.user.email });
          identifyUser(session.user.id, { email: session.user.email });
        }
      } catch (err) {
        const friendly = await parseSupabaseError(err as Error);
        logger.error('Auth session load failed', err);
        captureFriendlyError(err, friendly);
        if (!mounted) return;
        toast.error(friendly.title, { description: friendly.message });
        updateState({ isAuthLoading: false });
      }
    })();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION') return;

      if (session?.user?.id) {
        updateState({ session, user: session.user });
        if (event === 'SIGNED_IN') {
          setSentryUser({ id: session.user.id, email: session.user.email });
          identifyUser(session.user.id, { email: session.user.email });
        }
      } else {
        updateState({
          session: null,
          user: null,
          profile: null,
          isAuthLoading: false,
          isProfileLoading: false,
        });
        setSentryUser(null);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (state.isAuthLoading) return;
    if (!state.user?.id) return;
    loadProfile(state.user.id);
  }, [state.isAuthLoading, state.user?.id]);

  const refreshProfile = async () => {
    if (state.user?.id) {
      const profile = await fetchProfile(state.user.id);
      updateState({ profile });
    }
  };

  const signInWithEmail = async (
    email: string
  ): Promise<{ error: FriendlyError | null }> => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: true },
      });
      if (error) {
        const friendly = await parseSupabaseError(error);
        captureFriendlyError(error, friendly);
        return { error: friendly };
      }
      return { error: null };
    } catch (err) {
      const friendly = await parseSupabaseError(err as Error);
      captureFriendlyError(err, friendly);
      return { error: friendly };
    }
  };

  const verifyOtp = async (
    email: string,
    token: string
  ): Promise<{ error: FriendlyError | null }> => {
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'email',
      });
      if (error) {
        const friendly = await parseSupabaseError(error);
        captureFriendlyError(error, friendly);
        return { error: friendly };
      }
      const isNewSignup =
        !!data.user &&
        !!data.user.created_at &&
        data.user.created_at === data.user.last_sign_in_at;
      track(isNewSignup ? 'signup_completed' : 'login_completed', {
        method: 'email',
      });
      return { error: null };
    } catch (err) {
      const friendly = await parseSupabaseError(err as Error);
      captureFriendlyError(err, friendly);
      return { error: friendly };
    }
  };

  const signInWithGoogle = async () => {
    console.log('Google Sign-In not yet configured');
  };
  const signInWithApple = async () => {
    console.log('Apple Sign-In not yet configured');
  };
  const signOut = async () => {
    setSentryUser(null);
    resetAnalytics();
    await supabase.auth.signOut();
    updateState({ profile: null });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        signInWithEmail,
        verifyOtp,
        signInWithGoogle,
        signInWithApple,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
